import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardHeader,
  Divider,
  TableHead,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import { userApi } from '../../../api/user-api';
import { useMounted } from '../../../hooks/use-mounted';
import { Scrollbar } from '../../scrollbar';
import { SeverityPill } from '../../severity-pill';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import moment from 'moment';

export const CustomerLogs = (props) => {
  const { admin_id, customerid } = props;
  const isMounted = useMounted();
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedType, setSelectedType] = useState(null);

  const observer = useRef();

  const handleSortClick = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const descendingComparator = (a, b) => {
    const aDate = moment(a["created_at"], 'DD/MM/YYYY HH:mm:ss').toISOString();
    const bDate = moment(b["created_at"], 'DD/MM/YYYY HH:mm:ss').toISOString();
    if (bDate < aDate) return -1;
    if (bDate > aDate) return 1;
    return 0;
  };

  const getComparator = (order) => (order === 'desc'
    ? (a, b) => descendingComparator(a, b)
    : (a, b) => -descendingComparator(a, b));

  const applySort = (logsToSort, sort) => {
    const comparator = getComparator(sort);
    const stabilized = logsToSort.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
      const result = comparator(a[0], b[0]);
      return result !== 0 ? result : a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  };

  const loadMore = useCallback(async () => {
    try {
      const data = await userApi.getUsersActivities(admin_id, customerid, page);
      if (isMounted()) {
        setLogs(prev => [...prev, ...data.results]);
        setPage(prev => prev + 1);
        setHasMore(data.has_next);
      }
    } catch (err) {
      console.error(err);
    }
  }, [admin_id, customerid, page, isMounted]);

  useEffect(() => {
    loadMore();
  }, []);

  const lastLogRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });
    if (node) observer.current.observe(node);
  }, [hasMore, loadMore]);

  const filteredLogs = logs.filter(log => selectedType === null || log.type === selectedType);
  const sortedLogs = applySort(filteredLogs, sortDirection);
  const types = [...new Set(logs.map((log) => log.type))];

  return (
    <Card {...props}>
      <CardHeader title="Activités" />
      <Divider />
      <Scrollbar>
        <Select
          value={selectedType}
          displayEmpty
          onChange={(event) => {
            setSelectedType(event.target.value);
          }}
          sx={{
            minWidth: '10em',
            maxHeight: '3.5em',
            backgroundColor: '#F8F8F8',
            borderRadius: '4px',
            padding: '8px',
            margin: '2%',
            border: 'none',
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#555B61" : "#FFFFFF"
          }}
          IconComponent={ArrowDropDown}
        >
          <MenuItem value={null}>Tous les types</MenuItem>
          {types.map((type) => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </Select>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">Type</TableCell>
              <TableCell align="center">Description</TableCell>
              <TableCell align="center">
                <Typography
                  onClick={handleSortClick}
                  style={{ cursor: 'pointer' }}
                >
                  Date de création
                  {sortDirection === 'desc'
                    ? <ArrowDropDown fontSize="small" />
                    : <ArrowDropUp fontSize="small" />}
                </Typography>
              </TableCell>
              <TableCell align="center">ID du cours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedLogs.map((log, index) => {
              const isLast = index === sortedLogs.length - 1;
              return (
                <TableRow key={log.id} ref={isLast ? lastLogRef : null}>
                  <TableCell align="center">
                    <SeverityPill color={log.type !== 'acces' ? 'success' : 'warning'}>
                      {log.type}
                    </SeverityPill>
                  </TableCell>
                  <TableCell align="center">{log.description}</TableCell>
                  <TableCell align="center">{log.created_at}</TableCell>
                  <TableCell align="center">{log.courses}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Scrollbar>
    </Card>
  );
};
